import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';

import { useAuthContext } from '~/shared/context/useAuthContext';

import { shouldShowFullGraph } from '../UtilizationCharts/helpers';

import { DashedLineSwatch } from './chartSwatches';
import { CHART_COLORS } from './helpers';

export type Labels = 'utilized' | 'size' | 'sourceSize' | 'datafyDiskUsed';
export type ChartTypes = 'utilization' | 'usage';
interface LegendProps {
  onLabelToggle: (label: Labels) => void;
  visibilityStatus: { [key in Labels]: boolean };
  chartType: ChartTypes;
}

interface LegendItemProps {
  label: string;
  isVisible: boolean;
  onClick: () => void;
  color?: string;
  icon?: React.ReactNode;
}

function LegendItem({ label, isVisible, onClick, color, icon }: LegendItemProps) {
  const fontColor = clsx(
    'text-xs 2xl:text-sm 4xl:text-[16px] leading-[24px] font-bold text-[#949EAC] tracking-[-0.397px] whitespace-nowrap',
    isVisible ? 'opacity-100' : 'opacity-40'
  );

  return (
    <div className="flex gap-[4px] 2xl:gap-1.5 h-fit items-center cursor-pointer" onClick={onClick}>
      {icon || <div className="w-3 h-1" style={{ backgroundColor: color }} />}
      <div className={fontColor}>{label}</div>
    </div>
  );
}

export default function Legends({ onLabelToggle, visibilityStatus, chartType }: LegendProps) {
  const [searchParams] = useSearchParams();
  const { activeTenantId } = useAuthContext();

  const showFullGraph = shouldShowFullGraph(!!searchParams.get('full_graph'), activeTenantId);

  const dashedLineIcon = <DashedLineSwatch color={CHART_COLORS.SOURCE_SIZE} />;

  return (
    <div
      id="legend-container"
      className="h-[36px] w-fit mx-auto absolute -top-[56px] right-1/2 translate-x-1/2 flex justify-center"
    >
      <div className="flex gap-2 2xl:gap-3 4xl:gap-[32px] h-fit items-center">
        {chartType === 'utilization' && (
          <LegendItem
            label="Utilization %"
            isVisible={visibilityStatus.utilized}
            onClick={() => onLabelToggle('utilized')}
            color={CHART_COLORS.UTILIZED}
          />
        )}
        {chartType === 'usage' && (
          <>
            {showFullGraph && (
              <LegendItem
                label="Datafy Used"
                isVisible={visibilityStatus.datafyDiskUsed}
                onClick={() => onLabelToggle('datafyDiskUsed')}
                color={CHART_COLORS.DATAFY_DISK_USED}
              />
            )}
            <LegendItem
              label="Used"
              isVisible={visibilityStatus.utilized}
              onClick={() => onLabelToggle('utilized')}
              color={CHART_COLORS.UTILIZED}
            />
            <LegendItem
              label="Size"
              isVisible={visibilityStatus.size}
              onClick={() => onLabelToggle('size')}
              color={CHART_COLORS.SIZE}
            />
            <LegendItem
              label="Original Size"
              isVisible={visibilityStatus.sourceSize}
              onClick={() => onLabelToggle('sourceSize')}
              icon={dashedLineIcon}
            />
          </>
        )}
      </div>
    </div>
  );
}
